import { useState } from 'react';
import type { SalesPeriodFilter } from '../../../api/projectManagement';
import { formatDayMonthYear } from '../../../utils/dateFormat';

export function quarterLabel(quarter: string): string {
  return quarter.replace(/^(\d{4})-(Q\d)$/, '$2 $1');
}

function formatDay(day: string): string {
  return formatDayMonthYear(`${day}T00:00:00`);
}

const QUARTER_NUMBERS = ['Q1', 'Q2', 'Q3', 'Q4'] as const;

function yearQuarters(year: string): string[] {
  return QUARTER_NUMBERS.map((q) => `${year}-${q}`);
}

/**
 * Human label for a filter: full years collapse to "2026", other quarters are
 * listed ("Q3 2026 + Q4 2026"), a range shows its two dates.
 */
export function describePeriod(filter: SalesPeriodFilter | null): string {
  if (!filter) return 'all time';
  if (filter.from && filter.to) return `${formatDay(filter.from)} – ${formatDay(filter.to)}`;
  const selected = new Set(filter.quarters);
  const parts: string[] = [];
  const years = [...new Set(filter.quarters.map((q) => q.slice(0, 4)))].sort();
  for (const year of years) {
    const quarters = yearQuarters(year);
    if (quarters.every((q) => selected.has(q))) parts.push(year);
    else parts.push(...quarters.filter((q) => selected.has(q)).map(quarterLabel));
  }
  return parts.join(' + ');
}

const selectClass =
  'rounded-full border border-border-light bg-surface-primary px-3 py-1 text-body-xs text-text-primary outline-none focus:border-text-primary';

const dateInputClass =
  'rounded-lg border border-border-light bg-surface-primary px-2 py-1 text-body-xs outline-none focus:border-text-primary';

/** The option value a filter maps to: "all", "2026", "2026-Q4", "custom". */
function valueOf(filter: SalesPeriodFilter | null, customOpen: boolean): string {
  if (customOpen || (filter?.from && filter?.to)) return 'custom';
  if (!filter || filter.quarters.length === 0) return 'all';
  if (filter.quarters.length === 1) return filter.quarters[0]!;
  const year = filter.quarters[0]!.slice(0, 4);
  return yearQuarters(year).every((q) => filter.quarters.includes(q)) && filter.quarters.length === 4 ? year : 'custom';
}

/**
 * Period dropdown for the closed won/lost line (open pipeline is always
 * today's): all time, a year, a quarter, or a custom date range.
 */
export function PeriodFilter({
  quarterOptions,
  value,
  onChange,
}: {
  /** Quarters that have data, "YYYY-Qn", sorted. */
  quarterOptions: string[];
  value: SalesPeriodFilter | null;
  onChange: (next: SalesPeriodFilter | null) => void;
}) {
  const [customOpen, setCustomOpen] = useState(Boolean(value?.from));
  const [from, setFrom] = useState(value?.from ?? '');
  const [to, setTo] = useState(value?.to ?? '');

  const years = [...new Set(quarterOptions.map((q) => q.slice(0, 4)))].sort().reverse();
  const selectValue = valueOf(value, customOpen);

  const select = (next: string) => {
    if (next === 'custom') {
      setCustomOpen(true);
      return;
    }
    setCustomOpen(false);
    if (next === 'all') onChange(null);
    else if (/^\d{4}$/.test(next)) onChange({ quarters: yearQuarters(next), from: null, to: null });
    else onChange({ quarters: [next], from: null, to: null });
  };

  const applyRange = (nextFrom: string, nextTo: string) => {
    setFrom(nextFrom);
    setTo(nextTo);
    if (nextFrom && nextTo && nextFrom <= nextTo) onChange({ quarters: [], from: nextFrom, to: nextTo });
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <select className={selectClass} value={selectValue} onChange={(e) => select(e.target.value)} aria-label="Closed in">
        <option value="all">All time</option>
        {years.map((year) => (
          <optgroup key={year} label={year}>
            <option value={year}>Full year {year}</option>
            {[...quarterOptions]
              .filter((q) => q.startsWith(year))
              .reverse()
              .map((q) => (
                <option key={q} value={q}>
                  {quarterLabel(q)}
                </option>
              ))}
          </optgroup>
        ))}
        <option value="custom">Custom dates…</option>
      </select>

      {customOpen && (
        <div className="flex flex-wrap items-center gap-2 text-body-xs text-text-secondary">
          <input type="date" value={from} max={to || undefined} onChange={(e) => applyRange(e.target.value, to)} className={dateInputClass} aria-label="From" />
          <span>–</span>
          <input type="date" value={to} min={from || undefined} onChange={(e) => applyRange(from, e.target.value)} className={dateInputClass} aria-label="To" />
          {from && to && from > to && <span className="text-error">From must be on or before To</span>}
        </div>
      )}
    </div>
  );
}
